/**
 * Design token data for the PSI theme system — a leaf module of pure data.
 *
 * This file is the single source of truth for the default theme: raw hex
 * colors (`DefaultHexColor`), the semantic color palette, the spacing scale,
 * the six font family stacks, and the avatar color list — grouped together as
 * `defaultTheme` — plus the token type shapes (`Theme`, `PartialTheme`, and
 * the per-group token types). It imports nothing, so token-only consumers
 * (like the Preact teaser) can depend on it without pulling in any behavior.
 * The functions that compose and serialize these tokens live in
 * `theme.helpers.ts`.
 */

/**
 * Raw hex color palette used as defaults for the design system theme. Partner
 * silos may consume specific entries when overriding theme tokens.
 * @public
 */
export enum DefaultHexColor {
    white = '#FFFFFF',
    black = '#000000',
    iris = '#5A1EF5',
    iris20Percent = '#5A1EF533',
    lavender = '#7D7DFF',
    tanzanite1 = '#3F3F80',
    tanzanite2 = '#7F7FC0',
    tanzanite3 = '#ECECFF',
    jasper1 = '#7D6453',
    jasper3 = '#FEF7F2',
    jasper3Shade1 = '#F6F0EC',
    jasper3Shade2 = '#EDE4DE',
    kilauea1 = '#F53C19',
    kilauea2 = '#FA7355',
    kilauea3 = '#FFB4A5',
    garnet1 = '#763A35',
    garnet2 = '#B57A75',
    garnet3 = '#FCEBEA',
    saguaro1 = '#A57328',
    saguaro2 = '#FFC841',
    saguaro3 = '#FFE6B4',
    mum = '#00AF87',
    hoh1 = '#058287',
    hoh2 = '#91D7BE',
    hoh3 = '#B9EBDC',
    hydrangea1 = '#CDE1FF',
    hydrangea3 = '#F5F9FF',
    hydrangea3Shade = '#E8EFF9',
    greyBlue = '#DCE0E5',
    grey1 = '#F7F7F7',
    grey2 = '#E9E9E9',
    grey3 = '#D9D9D9',
    grey4 = '#818181',
    grey5 = '#65676B',
    grey6 = '#2B2B2B',
    softBlack = '#0F0F0F',
    // TODO: The design says Soft Black at 15% - wouldn't 15% be 26? But we've
    // been using 33 until now
    softBlack15Percent = '#0F0F0F33',
    beetle = '#A95851',
    carnation1 = '#C34646',
    carnation2 = '#EB7369',
    carnation3 = '#F07D7D',
    elsinore1 = '#F56400',
    pisgah1 = '#553CD7',
    timucuan1 = '#BE73CD',
    emperor = '#525DCF',
    gossamer = '#90AACF',
}

/**
 * Semantic color roles of the design system. Every key becomes a CSS custom
 * property of the same name (prefixed with `--`) on the web.
 *
 * The skeleton pair: `colorSkeletonPrimary` is the placeholder fill and
 * `colorSkeletonSecondary` is the color the loading wave fades it toward.
 * @public
 */
export type ColorTokens = {
    // Text
    colorTextPrimary: string;
    colorTextSecondaryOnBanner: string;
    colorTextSecondary: string;
    colorTextDisabled: string;
    colorTextHint: string;
    colorTextAccent: string;
    colorTextWarning: string;
    colorTextSuccess: string;
    colorTextOnPrimaryButton: string;
    colorTextOnAccentColorBg: string;
    colorTextOnEntryPointColorBg: string;
    colorTextOnTagLight: string;
    colorTextOnToastDark: string;
    colorTextOnDataVizCategory: string;
    colorTextOnTeaserButton: string;

    // Buttons
    colorButtonPrimary: string;
    colorButtonAccent: string;
    colorButtonSecondary: string;
    colorButtonDisabled: string;
    colorButtonSliderThumb: string;
    colorButtonTeaser: string;

    // Surface
    colorSurfacePrimary: string;
    colorSurfaceSecondary: string;
    colorSurfaceHighlight: string;
    colorSurfaceHighlightShade1: string;
    colorSurfaceHighlightShade2: string;

    // Skeleton
    colorSkeletonPrimary: string;
    colorSkeletonSecondary: string;
    colorSurfaceBannerWarning: string;
    colorSurfaceBannerSuggestion: string;
    colorSurfaceBannerSuccess: string;
    colorSurfaceBannerInfo: string;
    colorSurfaceUserBlock: string;
    colorSurfaceUserWarning: string;
    colorSurfaceUserUnblock: string;
    colorSurfaceUserVerified: string;
    colorSurfacePopoverDark: string;
    colorSurfaceGradient: string;
    colorSurfaceSliderPulse: string;
    colorSurfaceToggleBackground: string;

    // Tag
    colorTagDefault: string;
    colorTagAccent: string;
    colorTagAccentLight: string;
    colorTagHighlight: string;
    colorTagWarning: string;
    colorTagSuccess: string;
    colorTagVerified: string;
    colorTagWhite: string;

    // Border
    colorBorderPrimary: string;
    colorBorderPrimaryOnBanner: string;
    colorBorderSelected: string;
    colorBorderAccent: string;
    colorBorderAccentLight: string;
    colorBorderAccentOnDarkBg: string;
    colorBorderWarning: string;
    colorBorderFace: string;
    colorBorderSliderThumb: string;
    colorBorderDataVizBar: string;

    // Data Viz
    colorDataVizScale1: string;
    colorDataVizScale2: string;
    colorDataVizScale3: string;
    colorDataVizOther: string;
    colorDataVizCategory1: string;
    colorDataVizCategory2: string;
    colorDataVizCategory3: string;
    colorDataVizCategory4: string;
    colorDataVizCategory5: string;

    // Entry Point
    colorEntryPointColorGreen: string;
    colorEntryPointColorGreenTint: string;
    colorEntryPointColorBrown: string;
    colorEntryPointColorBrownTint: string;
    colorEntryPointColorRed: string;
    colorEntryPointColorRedTint: string;
    colorEntryPointColorPurple: string;
    colorEntryPointColorPurpleTint: string;
    colorEntryPointColorBlue: string;
    colorEntryPointColorBlueTint: string;

    // Brand – Primary
    colorPrimaryBrandWhite: string;
    colorPrimaryBrandBlack: string;
    colorPrimaryBrandIris: string;
    colorPrimaryBrandLavender: string;
    colorPrimaryBrandTanzanite3: string;

    // Brand – Secondary
    colorSecondaryBrandJasper3: string;
    colorSecondaryBrandJasper3Shade1: string;
    colorSecondaryBrandJasper3Shade2: string;

    // Utility
    colorUtilityKilauea1: string;
    colorUtilityKilauea2: string;
    colorUtilityGarnet3: string;
    colorUtilitySaguaro2: string;
    colorUtilitySaguaro3: string;
    colorUtilityMum: string;
    colorUtilityHoh3: string;
    colorUtilityHydrangea1: string;
    colorUtilityHydrangea3: string;
    colorUtilityGreyBlue: string;

    // Greys
    colorGrey1: string;
    colorGrey2: string;
    colorGrey3: string;
    colorGrey4: string;
    colorGrey5: string;
    colorGrey6: string;
    colorSoftBlack: string;

    // Extended
    colorExtendedJasper1: string;
    colorExtendedGarnet1: string;
    colorExtendedGarnet2: string;
    colorExtendedBeetle: string;
    colorExtendedCarnation1: string;
    colorExtendedCarnation2: string;
    colorExtendedElsinore1: string;
    colorExtendedSaguaro1: string;
    colorExtendedHoh1: string;
    colorExtendedHoh2: string;
    colorExtendedPisgah1: string;
    colorExtendedTimucuan1: string;
    colorExtendedTanzanite1: string;
    colorExtendedTanzanite2: string;
    colorExtendedEmperor: string;
    colorExtendedGossamer: string;

    // Illustration – Palette 1
    colorIllustration1Jasper3: string;
    colorIllustration1Jasper3Shade1: string;
    colorIllustration1Hydrangea3: string;
    colorIllustration1Hydrangea3Shade: string;
    colorIllustration1Kilauea1: string;
    colorIllustration1Kilauea3: string;
    colorIllustration1Hoh1: string;
    colorIllustration1Tanzanite1: string;

    // Illustration – Palette 2
    colorIllustration2Emperor: string;
    colorIllustration2Hydrangea1: string;
    colorIllustration2Hoh1: string;
    colorIllustration2Mum: string;
    colorIllustration2Hoh2: string;
    colorIllustration2Garnet2: string;
    colorIllustration2Carnation1: string;
    colorIllustration2Carnation3: string;
    colorIllustration2Kilauea2: string;
    colorIllustration2Saguaro2: string;
};

/**
 * Named steps of the spacing scale, in logical pixels. Each key becomes a
 * `--spacing-{key}` CSS custom property (with a `px` suffix) on the web.
 * @public
 */
export type SpacingTokens = {
    xxs: number;
    xs: number;
    sm: number;
    md: number;
    lg: number;
    xl: number;
    xl2: number;
    xl3: number;
    xl4: number;
    xl5: number;
};

/**
 * Font family stacks of the design system. Every key becomes a CSS custom
 * property of the same name (prefixed with `--`) on the web.
 * @public
 */
export type FontTokens = {
    fontFamilySansRegular: string;
    fontFamilySansMedium: string;
    fontFamilySansSemiBold: string;
    fontFamilyMonoRegular: string;
    fontFamilyMonoMedium: string;
    fontFamilyMonoSemiBold: string;
};

/**
 * A complete, resolved set of design tokens, grouped by kind.
 * @public
 */
export type Theme = {
    colors: ColorTokens;
    spacing: SpacingTokens;
    fonts: FontTokens;
    avatarColors: string[];
};

/**
 * A sparse theme override: any subset of the overridable token groups, each
 * overriding any subset of that group's tokens. `avatarColors` is replaced
 * wholesale when present (it is an ordered list, not a keyed record).
 *
 * Spacing is deliberately absent. It is design-system rhythm rather than brand,
 * and it feeds layout arithmetic — scroll extents, overlay offsets — that no
 * partner value has been validated against. Every theme therefore resolves to
 * the default spacing scale.
 * @public
 */
export type PartialTheme = {
    colors?: Partial<ColorTokens>;
    fonts?: Partial<FontTokens>;
    avatarColors?: string[];
};

const DEFAULT_COLOR_PALETTE: Record<keyof ColorTokens, DefaultHexColor> = {
    // Text: Controls typography across the app to ensure proper contrast.
    colorTextPrimary: DefaultHexColor.black,
    colorTextSecondaryOnBanner: DefaultHexColor.grey6,
    colorTextSecondary: DefaultHexColor.grey5,
    colorTextDisabled: DefaultHexColor.grey4,
    colorTextHint: DefaultHexColor.grey4,
    colorTextAccent: DefaultHexColor.iris,
    colorTextWarning: DefaultHexColor.kilauea1,
    colorTextSuccess: DefaultHexColor.mum,
    colorTextOnPrimaryButton: DefaultHexColor.white,
    colorTextOnAccentColorBg: DefaultHexColor.white,
    colorTextOnEntryPointColorBg: DefaultHexColor.white,
    colorTextOnTagLight: DefaultHexColor.black,
    colorTextOnToastDark: DefaultHexColor.white,
    colorTextOnDataVizCategory: DefaultHexColor.white,
    colorTextOnTeaserButton: DefaultHexColor.black,

    // Buttons: Specifically manages the coloring and states of buttons.
    colorButtonPrimary: DefaultHexColor.softBlack,
    colorButtonAccent: DefaultHexColor.iris,
    colorButtonSecondary: DefaultHexColor.white,
    colorButtonDisabled: DefaultHexColor.grey2,
    colorButtonSliderThumb: DefaultHexColor.white,
    colorButtonTeaser: DefaultHexColor.white,

    // Surface: Defines the backgrounds and surfaces of the app.
    colorSurfacePrimary: DefaultHexColor.white,
    colorSurfaceSecondary: DefaultHexColor.grey1,
    colorSurfaceHighlight: DefaultHexColor.jasper3,
    colorSurfaceHighlightShade1: DefaultHexColor.jasper3Shade1,
    colorSurfaceHighlightShade2: DefaultHexColor.jasper3Shade2,

    // Skeleton: the placeholder fill and the color the wave fades it toward.
    colorSkeletonPrimary: DefaultHexColor.grey2,
    colorSkeletonSecondary: DefaultHexColor.grey1,
    colorSurfaceBannerWarning: DefaultHexColor.garnet3,
    colorSurfaceBannerSuggestion: DefaultHexColor.jasper3,
    colorSurfaceBannerSuccess: DefaultHexColor.hoh3,
    colorSurfaceBannerInfo: DefaultHexColor.hydrangea3,
    colorSurfaceUserBlock: DefaultHexColor.kilauea1,
    colorSurfaceUserWarning: DefaultHexColor.saguaro2,
    colorSurfaceUserUnblock: DefaultHexColor.mum,
    colorSurfaceUserVerified: DefaultHexColor.hydrangea1,
    colorSurfacePopoverDark: DefaultHexColor.softBlack,
    colorSurfaceGradient: DefaultHexColor.tanzanite3,
    colorSurfaceSliderPulse: DefaultHexColor.iris20Percent,
    colorSurfaceToggleBackground: DefaultHexColor.grey3,

    // Tag: Colors used for topic tags, badges, and labels
    colorTagDefault: DefaultHexColor.grey1,
    colorTagAccent: DefaultHexColor.iris,
    colorTagAccentLight: DefaultHexColor.tanzanite3,
    colorTagHighlight: DefaultHexColor.garnet3,
    colorTagWarning: DefaultHexColor.garnet3,
    colorTagSuccess: DefaultHexColor.hoh3,
    colorTagVerified: DefaultHexColor.hydrangea1,
    colorTagWhite: DefaultHexColor.white,

    // Border: Used for dividers, input outlines and indicating selected states.
    colorBorderPrimary: DefaultHexColor.grey3,
    colorBorderPrimaryOnBanner: DefaultHexColor.softBlack15Percent,
    colorBorderSelected: DefaultHexColor.softBlack,
    colorBorderAccent: DefaultHexColor.iris,
    colorBorderAccentLight: DefaultHexColor.tanzanite3,
    colorBorderAccentOnDarkBg: DefaultHexColor.lavender,
    colorBorderWarning: DefaultHexColor.kilauea1,
    colorBorderFace: DefaultHexColor.white,
    colorBorderSliderThumb: DefaultHexColor.grey5,
    colorBorderDataVizBar: DefaultHexColor.white,

    // Data Viz: charts, graphs, and category labels.
    colorDataVizScale1: DefaultHexColor.iris,
    colorDataVizScale2: DefaultHexColor.lavender,
    colorDataVizScale3: DefaultHexColor.tanzanite3,
    colorDataVizOther: DefaultHexColor.greyBlue,
    colorDataVizCategory1: DefaultHexColor.emperor,
    colorDataVizCategory2: DefaultHexColor.timucuan1,
    colorDataVizCategory3: DefaultHexColor.garnet1,
    colorDataVizCategory4: DefaultHexColor.saguaro1,
    colorDataVizCategory5: DefaultHexColor.elsinore1,

    // Entry Point: Theming colors specifically assigned to teasers.
    colorEntryPointColorGreen: DefaultHexColor.hoh1,
    colorEntryPointColorGreenTint: DefaultHexColor.hoh2,
    colorEntryPointColorBrown: DefaultHexColor.beetle,
    colorEntryPointColorBrownTint: DefaultHexColor.garnet2,
    colorEntryPointColorRed: DefaultHexColor.carnation1,
    colorEntryPointColorRedTint: DefaultHexColor.carnation2,
    colorEntryPointColorPurple: DefaultHexColor.tanzanite1,
    colorEntryPointColorPurpleTint: DefaultHexColor.tanzanite2,
    colorEntryPointColorBlue: DefaultHexColor.emperor,
    colorEntryPointColorBlueTint: DefaultHexColor.gossamer,

    // Brand – Primary: The core identity colors used for logos and identity.
    colorPrimaryBrandWhite: DefaultHexColor.white,
    colorPrimaryBrandBlack: DefaultHexColor.black,
    colorPrimaryBrandIris: DefaultHexColor.iris,
    colorPrimaryBrandLavender: DefaultHexColor.lavender,
    colorPrimaryBrandTanzanite3: DefaultHexColor.tanzanite3,

    // Brand – Secondary:
    colorSecondaryBrandJasper3: DefaultHexColor.jasper3,
    colorSecondaryBrandJasper3Shade1: DefaultHexColor.jasper3Shade1,
    colorSecondaryBrandJasper3Shade2: DefaultHexColor.jasper3Shade2,

    // Utility: colors used to communicate system status to the user.
    colorUtilityKilauea1: DefaultHexColor.kilauea1,
    colorUtilityKilauea2: DefaultHexColor.kilauea2,
    colorUtilityGarnet3: DefaultHexColor.garnet3,
    colorUtilitySaguaro2: DefaultHexColor.saguaro2,
    colorUtilitySaguaro3: DefaultHexColor.saguaro3,
    colorUtilityMum: DefaultHexColor.mum,
    colorUtilityHoh3: DefaultHexColor.hoh3,
    colorUtilityHydrangea1: DefaultHexColor.hydrangea1,
    colorUtilityHydrangea3: DefaultHexColor.hydrangea3,
    colorUtilityGreyBlue: DefaultHexColor.greyBlue,

    // Greys: Neutral shades used for subtle backgrounds, borders, or text.
    colorGrey1: DefaultHexColor.grey1,
    colorGrey2: DefaultHexColor.grey2,
    colorGrey3: DefaultHexColor.grey3,
    colorGrey4: DefaultHexColor.grey4,
    colorGrey5: DefaultHexColor.grey5,
    colorGrey6: DefaultHexColor.grey6,
    colorSoftBlack: DefaultHexColor.softBlack,

    // Extended: A broader palette of varied colors.
    colorExtendedJasper1: DefaultHexColor.jasper1,
    colorExtendedGarnet1: DefaultHexColor.garnet1,
    colorExtendedGarnet2: DefaultHexColor.garnet2,
    colorExtendedBeetle: DefaultHexColor.beetle,
    colorExtendedCarnation1: DefaultHexColor.carnation1,
    colorExtendedCarnation2: DefaultHexColor.carnation2,
    colorExtendedElsinore1: DefaultHexColor.elsinore1,
    colorExtendedSaguaro1: DefaultHexColor.saguaro1,
    colorExtendedHoh1: DefaultHexColor.hoh1,
    colorExtendedHoh2: DefaultHexColor.hoh2,
    colorExtendedPisgah1: DefaultHexColor.pisgah1,
    colorExtendedTimucuan1: DefaultHexColor.timucuan1,
    colorExtendedTanzanite1: DefaultHexColor.tanzanite1,
    colorExtendedTanzanite2: DefaultHexColor.tanzanite2,
    colorExtendedEmperor: DefaultHexColor.emperor,
    colorExtendedGossamer: DefaultHexColor.gossamer,

    // Illustration – Palette 1: Specific color combinations reserved for SVGs.
    colorIllustration1Jasper3: DefaultHexColor.jasper3,
    colorIllustration1Jasper3Shade1: DefaultHexColor.jasper3Shade1,
    colorIllustration1Hydrangea3: DefaultHexColor.hydrangea3,
    colorIllustration1Hydrangea3Shade: DefaultHexColor.hydrangea3Shade,
    colorIllustration1Kilauea1: DefaultHexColor.kilauea1,
    colorIllustration1Kilauea3: DefaultHexColor.kilauea3,
    colorIllustration1Hoh1: DefaultHexColor.hoh1,
    colorIllustration1Tanzanite1: DefaultHexColor.tanzanite1,

    // Illustration – Palette 2
    colorIllustration2Emperor: DefaultHexColor.emperor,
    colorIllustration2Hydrangea1: DefaultHexColor.hydrangea1,
    colorIllustration2Hoh1: DefaultHexColor.hoh1,
    colorIllustration2Mum: DefaultHexColor.mum,
    colorIllustration2Hoh2: DefaultHexColor.hoh2,
    colorIllustration2Garnet2: DefaultHexColor.garnet2,
    colorIllustration2Carnation1: DefaultHexColor.carnation1,
    colorIllustration2Carnation3: DefaultHexColor.carnation3,
    colorIllustration2Kilauea2: DefaultHexColor.kilauea2,
    colorIllustration2Saguaro2: DefaultHexColor.saguaro2,
};

const DEFAULT_SPACING: SpacingTokens = {
    xxs: 4,
    xs: 8,
    sm: 12,
    md: 16,
    lg: 20,
    xl: 24,
    xl2: 32,
    xl3: 40,
    xl4: 60,
    xl5: 80,
};

const DEFAULT_FONTS: FontTokens = {
    fontFamilySansRegular: 'IBMPlexSans_400Regular, Arial, Helvetica, sans-serif',
    fontFamilySansMedium: 'IBMPlexSans_500Medium, Arial, Helvetica, sans-serif',
    fontFamilySansSemiBold: 'IBMPlexSans_600SemiBold, Arial, Helvetica, sans-serif',
    fontFamilyMonoRegular: 'IBMPlexMono_400Regular, Arial, Helvetica, sans-serif',
    fontFamilyMonoMedium: 'IBMPlexMono_500Medium, Arial, Helvetica, sans-serif',
    fontFamilyMonoSemiBold: 'IBMPlexMono_600SemiBold, Arial, Helvetica, sans-serif',
};

const DEFAULT_AVATAR_COLORS: string[] = [
    DefaultHexColor.jasper1,
    DefaultHexColor.garnet1,
    DefaultHexColor.garnet2,
    DefaultHexColor.beetle,
    DefaultHexColor.carnation1,
    DefaultHexColor.kilauea1,
    DefaultHexColor.elsinore1,
    DefaultHexColor.saguaro1,
    DefaultHexColor.hoh1,
    DefaultHexColor.pisgah1,
    DefaultHexColor.timucuan1,
    DefaultHexColor.tanzanite1,
    DefaultHexColor.tanzanite2,
    DefaultHexColor.emperor,
    DefaultHexColor.lavender,
];

/**
 * The complete default token set of the PSI design system. Consumers that
 * render without a `ThemeProvider` resolve tokens from here.
 * @public
 */
export const defaultTheme: Theme = {
    colors: DEFAULT_COLOR_PALETTE,
    spacing: DEFAULT_SPACING,
    fonts: DEFAULT_FONTS,
    avatarColors: DEFAULT_AVATAR_COLORS,
};
